import { Component, inject, signal } from "@angular/core";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { ROUTES } from "../../constants/routes";
import { Navbar } from "../../components/navbar/navbar";
import { Breadcrumb } from "../../components/breadcrumb/breadcrumb";
import { Footer } from "../../components/footer/footer";
import { SeoService } from "../../services/seo.service";
import { SITE } from "../../constants/site";
import { ICONS } from "../../constants/icons";
import { Article, ArticleSlug, findArticle, relatedArticles } from "../../constants/articles";
import {
  articleSchema,
  breadcrumbSchema,
  faqSchema,
  webPageSchema
} from "../../helpers/structured-data";

@Component({
  selector: "app-article",
  imports: [RouterLink, Breadcrumb, Navbar, Footer],
  templateUrl: "./article.html",
  styleUrl: "./article.scss"
})
export class ArticlePage {
  readonly routes = ROUTES;
  readonly site = SITE;
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly seo = inject(SeoService);
  private readonly sanitizer = inject(DomSanitizer);

  readonly article = signal<Article | undefined>(undefined);
  readonly related = signal<Article[]>([]);

  iconFor(name: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(ICONS[name] ?? "");
  }

  /** Anchor id for a section heading so the table of contents can link to it. */
  anchorFor(heading: string): string {
    return heading
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  formatDate(iso: string): string {
    return new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
      timeZone: "UTC"
    });
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      const slug = params.get("slug") ?? "";
      const found = findArticle(slug as ArticleSlug);

      if (!found) {
        this.router.navigate([ROUTES.resources.url]);
        return;
      }

      this.article.set(found);
      this.related.set(relatedArticles(found));
      this.applySeo(found);
    });
  }

  private applySeo(article: Article): void {
    this.seo.apply({
      title: article.metaTitle,
      description: article.metaDescription,
      path: `/resources/${article.slug}`,
      keywords: article.keywords,
      type: "article",
      structuredData: [
        breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Resources", path: "/resources" },
          { name: article.title, path: `/resources/${article.slug}` }
        ]),
        webPageSchema({
          name: article.metaTitle,
          description: article.metaDescription,
          path: `/resources/${article.slug}`
        }),
        articleSchema(article),
        faqSchema(article.faqs)
      ]
    });
  }
}
